'use client';
import { Box, Heading, Text, useToast } from '@chakra-ui/react';
import { Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import ButtonCustom from '@/app/_components/button/ButtonCustom';
import {
  FUEL_TYPES,
  ODOMETER_OPTIONS,
  SELL_YOUR_CAR_STEPS,
  TRANSMISSION_TYPES,
  VEHICLE_STATES,
} from '@/constants/sell-your-car/sell-your-car.constants';
import { Province } from '@/services/province/province.service';

import { submitFormData } from '../_helpers/formSubmissionHelpers';
import { SellYourFormValues, TransformedBrand } from '../service';

import { ExpectedPriceInput } from './form/ExpectedPriceStep';
import FormHeader from './form/FormHeader';
import PhotoUpload from './form/PhotoUpload';
import { SuccessMessage } from './form/SuccessMessageStep';
import { UserDetailsForm } from './form/UsedDetailsStep';
import VehicleBasicInfoStep from './form/VehicleBasicInfoStep';
import VehicleDetailsStep from './form/VehicleDetailsStep';
import VehicleSelectionStep from './form/VehicleSelectionStep';
import Styles from './SellYourCarForm.module.scss';

type StepValidationMap = {
  [key in number]: Array<keyof SellYourFormValues>;
};

const stepValidationFields: StepValidationMap = {
  0: ['province', 'vehicle_year', 'custom_vehicle_brand', 'vehicle_range'],
  1: ['vehicle_fuel'],
  2: ['vehicle_transmission'],
  3: ['vehicle_kms'],
  4: ['vehicle_state'],
  5: [],
  6: ['vehicle_expected_price'],
  7: ['user_name', 'email', 'phone_number'],
  8: [],
} as const;

const SellYourFormValuesForm = ({
  onClose,
  makeAndModelData,
  provincesData = [],
}: {
  onClose?: () => void;
  makeAndModelData: TransformedBrand[];
  provincesData?: Province[];
}) => {
  const [step, setStep] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const toast = useToast();

  const {
    control,
    handleSubmit,
    trigger,
    formState: { errors, isSubmitting: formIsSubmitting },
    getValues,
    setValue,
  } = useForm<SellYourFormValues>({
    defaultValues: {
      province: undefined,
      vehicle_year: undefined,
      vehicle_brand: undefined,
      vehicle_range: undefined,
      vehicle_model: undefined,
      vehicle_fuel: undefined,
      vehicle_transmission: '',
      vehicle_kms: null,
      message: '',
      vehicle_state: '',
      photos: [],
      email: '',
      phone_number: '',
      user_name: '',
      vehicle_expected_price: '',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (step === 7) {
      const formValues = getValues();
      const requiredFieldsValid =
        formValues.province &&
        formValues.vehicle_year &&
        formValues.vehicle_brand &&
        formValues.vehicle_range &&
        formValues.vehicle_fuel &&
        formValues.vehicle_transmission &&
        formValues.vehicle_kms &&
        formValues.vehicle_state;

      if (!requiredFieldsValid) {
        setStep(0);
        toast({
          title: 'Please complete vehicle details first',
          description:
            'You need to provide vehicle information before submitting personal details.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  }, [step, getValues, toast]);

  const getCurrentStepFields = () => {
    return stepValidationFields[step] || [];
  };

  const onSubmit = async (data: SellYourFormValues) => {
    if (step < SELL_YOUR_CAR_STEPS.length - 1 && step !== 7) {
      setStep((s) => s + 1);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await submitFormData(data);

      if (result.success) {
        setStep(8);
        toast({
          title: 'Success!',
          description: 'Your car details have been submitted successfully.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        setSubmitError(
          result.error || 'Failed to submit your request. Please try again.',
        );
        toast({
          title: 'Submission failed',
          description:
            result.error || 'Failed to submit your request. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error during form submission:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to submit your request. Please try again.';

      setSubmitError(errorMessage);
      toast({
        title: 'Submission failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep((s) => s - 1);
    } else if (onClose) {
      onClose();
    }
  };

  const handleNext = async () => {
    const fieldsToValidate = getCurrentStepFields();

    if (fieldsToValidate.length === 0) {
      setStep((s) => s + 1);
      return;
    }

    const isValid = await trigger(fieldsToValidate as any);

    if (isValid) {
      if (step === 7) {
        handleSubmit(onSubmit)();
      } else {
        setStep((s) => s + 1);
      }
    }
  };

  const handleFinalSubmit = () => {
    trigger(['user_name', 'email', 'phone_number']).then((isValid) => {
      if (isValid) {
        handleSubmit(onSubmit)();
      }
    });
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <VehicleSelectionStep
            control={control}
            errors={errors}
            treeData={makeAndModelData}
            setValue={setValue}
            provincesData={provincesData}
            getValues={getValues}
          />
        );

      case 1:
        return (
          <VehicleDetailsStep
            key={
              typeof window !== 'undefined'
                ? crypto.randomUUID()
                : 'fallback-id'
            }
            control={control}
            errors={errors}
            options={FUEL_TYPES}
            name="vehicle_fuel"
            errorMessage="Please select a fuel type"
            colSize={6}
          />
        );

      case 2:
        return (
          <VehicleDetailsStep
            key={
              typeof window !== 'undefined'
                ? crypto.randomUUID()
                : 'fallback-id'
            }
            control={control}
            errors={errors}
            options={TRANSMISSION_TYPES}
            name="vehicle_transmission"
            errorMessage="Please select a transmission type"
            colSize={12}
          />
        );

      case 3:
        return (
          <VehicleBasicInfoStep
            control={control}
            errors={errors}
            name="vehicle_kms"
            options={ODOMETER_OPTIONS}
            placeholder="Select range"
            errorMessage="Before moving on, please select from the list provided"
          />
        );

      case 4:
        return (
          <VehicleDetailsStep
            key={
              typeof window !== 'undefined'
                ? crypto.randomUUID()
                : 'fallback-id'
            }
            control={control}
            errors={errors}
            options={VEHICLE_STATES}
            name="vehicle_state"
            errorMessage="Select one of the options before clicking NEXT"
            colSize={8}
          />
        );

      case 5:
        return (
          <PhotoUpload
            control={control}
            isDragging={isDragging}
            setIsDragging={setIsDragging}
          />
        );

      case 6:
        return <ExpectedPriceInput control={control} errors={errors} />;

      case 7:
        return <UserDetailsForm control={control} errors={errors} />;

      case 8:
        return <SuccessMessage />;

      default:
        return <Text>Something went wrong</Text>;
    }
  };

  const showBackButton = step >= 0;
  const showNextButton = step < SELL_YOUR_CAR_STEPS.length - 1 && step !== 8;

  return (
    <Box className={Styles.sellkiaformwrapper}>
      <Spin spinning={isSubmitting} fullscreen />
      <FormHeader
        step={step}
        totalSteps={SELL_YOUR_CAR_STEPS.length}
        onBack={handleBack}
        link={'/sell-my-car'}
        showBackButton={showBackButton}
      />

      <Box className={Styles.formContainer}>
        <Box className={Styles.formHeader}>
          <Heading
            as="h2"
            size="lg"
            mb={8}
            fontSize={28}
            textAlign="center"
            className={Styles.stepHeading}
            dangerouslySetInnerHTML={{
              __html: SELL_YOUR_CAR_STEPS[step] ?? '',
            }}
          />
        </Box>

        <form onSubmit={handleSubmit(onSubmit)} className={Styles.formBody}>
          {renderStep()}

          {submitError && (
            <Box mt={4} p={3} bg="red.50" color="red.600" borderRadius="md">
              <Text>{submitError}</Text>
            </Box>
          )}

          {showNextButton && (
            <Box mt={8} gap={4} className={Styles.formActions}>
              {step === 7 ? (
                <ButtonCustom
                  className="btnOutline"
                  moduleClass={Styles.ctaBtn}
                  onClick={handleFinalSubmit}
                  disabled={isSubmitting || formIsSubmitting}
                >
                  GET MY QUOTE {isSubmitting || formIsSubmitting ? '...' : ''}
                </ButtonCustom>
              ) : (
                <>
                  <ButtonCustom
                    className="btnOutline"
                    moduleClass={Styles.ctaBtn}
                    onClick={handleNext}
                    disabled={isSubmitting || formIsSubmitting}
                  >
                    Next {isSubmitting || formIsSubmitting ? '...' : ''}
                  </ButtonCustom>
                  {step === 5 && (
                    <ButtonCustom
                      moduleClass={Styles.ctaBtn}
                      onClick={() => {
                        setValue('photos', []);
                        setValue('message', '');
                        handleNext();
                      }}
                    >
                      Skip
                    </ButtonCustom>
                  )}
                </>
              )}
            </Box>
          )}
        </form>
      </Box>
    </Box>
  );
};

export default SellYourFormValuesForm;
